import { useState } from 'react';
import type { Book } from '@/mocks/bookData';
import { hotRanking, praiseRanking, hotTopicRanking, newBookRanking } from '@/mocks/bookData';

interface RankingItemProps {
  book: Book;
  rank: number;
}

function RankingItem({ book, rank }: RankingItemProps) {
  return (
    <article className="group flex gap-4 rounded-xl bg-background-50 p-3 transition hover:bg-background-100">
      <div className="relative w-16 shrink-0 overflow-hidden rounded-lg md:w-20">
        <img
          src={book.cover}
          alt={`《${book.title}》${book.author} 作品封面`}
          title={`${book.title} ${book.author}`}
          className="aspect-[3/4] w-full object-cover object-top"
        />
        <span
          className={`absolute left-1 top-1 flex h-6 w-6 items-center justify-center rounded-md text-xs font-bold text-background-50 ${
            rank <= 3 ? 'bg-primary-500' : 'bg-foreground-950/70'
          }`}
        >
          {rank}
        </span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate font-heading text-base font-bold text-foreground-950 md:text-lg">
              《{book.title}》
            </h3>
            <p className="mt-0.5 text-xs text-foreground-600">
              {book.author} · {book.wordCount} · {book.status}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1">
            <i className="ri-star-fill text-xs text-accent-600"></i>
            <span className="text-sm font-bold text-accent-800">{book.score.toFixed(1)}</span>
          </div>
        </div>

        <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-sm">
          {book.summary}
        </p>

        <div className="mt-auto flex items-center justify-between gap-2 pt-3">
          <div className="flex flex-wrap gap-1.5">
            {book.tags.map((t) => (
              <span
                key={t}
                className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900"
              >
                {t}
              </span>
            ))}
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <span className="flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-group-line"></i>
              {(book.readers / 1000).toFixed(1)}k 在读
            </span>
            <span
              className={`flex items-center gap-0.5 text-xs font-medium ${
                book.trend === 'up' ? 'text-accent-700' : book.trend === 'down' ? 'text-primary-700' : 'text-foreground-500'
              }`}
            >
              <i className={book.trend === 'up' ? 'ri-arrow-up-line' : book.trend === 'down' ? 'ri-arrow-down-line' : 'ri-loader-line'}></i>
              {book.trend === 'up' ? '上升' : book.trend === 'down' ? '回落' : '平稳'}
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}

const tabs = [
  { key: 'hot', label: '人气榜', icon: 'ri-fire-line' },
  { key: 'praise', label: '口碑榜', icon: 'ri-thumb-up-line' },
  { key: 'topic', label: '热议榜', icon: 'ri-chat-3-line' },
  { key: 'new', label: '新书榜', icon: 'ri-sparkling-line' },
] as const;

const sources: Record<string, Book[]> = {
  hot: hotRanking,
  praise: praiseRanking,
  topic: hotTopicRanking,
  new: newBookRanking,
};

export default function Rankings() {
  const [active, setActive] = useState<string>('hot');
  const list = sources[active];

  return (
    <section id="ranking" className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
            <i className="ri-trophy-line"></i>
            实时榜单
          </span>
          <h2 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            御宅书屋人气排行
          </h2>
          <p className="mt-2 max-w-xl text-sm text-foreground-600 md:text-base">
            聚合读者评分、热议作品与打卡数据，四类榜单每日更新，帮你快速锁定值得一读的作品。
          </p>
        </div>
        <a
          href="#recommend"
          className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-primary-700 hover:text-primary-800 whitespace-nowrap md:self-auto"
        >
          查看全部书单 <i className="ri-arrow-right-line"></i>
        </a>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActive(t.key)}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition whitespace-nowrap ${
              active === t.key
                ? 'bg-primary-500 text-background-50'
                : 'bg-secondary-100 text-secondary-900 hover:bg-secondary-200'
            }`}
          >
            <i className={t.icon}></i>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {list.map((book, idx) => (
          <RankingItem key={book.id} book={book} rank={idx + 1} />
        ))}
      </div>
    </section>
  );
}